import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import type { EvidenceItem, EvidenceItemDetail } from '../../lib/evidence.ts';
import type { ControlLite, HistoryRow } from '../../lib/ledgers.ts';
import { ftInput } from '../../components/ui/FormTable.tsx';
import { fmtDateTime } from '../../lib/date.ts';
import { useDepartments } from '../../lib/org.ts';

/** 대장 상세 공통: 통제항목 링크 · 이력 탭 · 증적 선택 */
export function ControlLinks({ controls }: { controls: ControlLite[] }) {
  if (!controls.length) return null;
  return <p className="text-[12px] text-slate-600 mb-2">통제항목: {controls.map((c) => <Link key={c.id} className="text-accent-strong hover:underline mr-2" to={`/controls/${c.id}`}><span className="font-mono">{c.code}</span> {c.title}</Link>)}</p>;
}
export function HistoryTab({ rows }: { rows: HistoryRow[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-5">
      <h2 className="font-semibold mb-1 cursor-pointer" onClick={() => setOpen(!open)}>이력 ({rows.length}) {open ? '▾' : '▸'}</h2>
      {open && <div className="bg-white overflow-x-auto border border-slate-200"><table className="w-full border-collapse dt-table"><thead><tr className="bg-slate-50 text-left">{['시각', '작업', '행위자', '변경'].map((h) => <th key={h} className="border-b border-slate-200 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{rows.map((h) => <tr key={h.id} className="dt-row"><td className="border-b border-slate-100 whitespace-nowrap text-[12px]">{fmtDateTime(h.occurredAt)}</td><td className="border-b border-slate-100">{h.op === 'I' ? '생성' : h.op === 'U' ? '수정' : '삭제'}</td><td className="border-b border-slate-100">{h.actorName}</td><td className="border-b border-slate-100 text-[12px]">{h.changed.map((c) => <div key={c.key}><span className="font-mono text-slate-500">{c.key}</span>: {fmt(c.before)} → {fmt(c.after)}</div>)}</td></tr>)}{rows.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-slate-500">없음</td></tr>}</tbody></table></div>}
    </section>
  );
}
function fmt(v: unknown) { if (v == null) return '—'; if (Array.isArray(v)) return v.join(', '); return String(v).slice(0, 60); }
export function EvidencePick({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const items = useQuery({ queryKey: ['evidence-items', 'picker', 'all'], queryFn: () => api<EvidenceItem[]>('/evidence/items') });
  const [itemId, setItemId] = useState('');
  const detail = useQuery({ queryKey: ['evidence-item', itemId], queryFn: () => api<EvidenceItemDetail>(`/evidence/items/${itemId}`), enabled: !!itemId });
  return (
    <span className="flex flex-wrap gap-1">
      <select className={ftInput + ' ft-w-md'} value={itemId} onChange={(e) => { setItemId(e.target.value); onChange(''); }}><option value="">증적 항목 (선택)</option>{items.data?.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}</select>
      {itemId && <select className={ftInput + ' ft-w-sm'} value={value} onChange={(e) => onChange(e.target.value)}><option value="">버전</option>{detail.data?.versions.map((v) => <option key={v.id} value={v.id}>v{v.versionNo} {v.originalName}</option>)}</select>}
    </span>
  );
}
/** 담당자(디렉터리) + 부서(활성 전체) 선택지 */
export function useDepts() {
  const dir = useQuery({ queryKey: ['directory'], queryFn: () => api<Array<{ id: string; name: string; department: { id: string; name: string } | null }>>('/users/directory') });
  const { depts } = useDepartments();
  return { users: dir.data ?? [], depts };
}
